import type { DepartmentNode, EmployeeListItem, Paginated } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronRight, CircleAlert, Plus } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { Avatar, Button, ErrorBox, Field, Input, PageHeader, Select, Spinner } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { plural } from '@/lib/format';
import { departmentsTreeQuery, employeesQuery, flattenTree } from '@/lib/queries';

const allEmployeesQuery = {
  queryKey: ['org', 'employees', 'all'],
  queryFn: () => api<Paginated<EmployeeListItem>>('/org/employees', { query: { pageSize: 200 } }),
  staleTime: 60_000,
};

export function OrgPage() {
  const tree = useQuery(departmentsTreeQuery);
  const search = useSearch({ strict: false }) as { department?: string };
  const navigate = useNavigate();
  const flat = useMemo(() => (tree.data ? flattenTree(tree.data) : []), [tree.data]);
  const selectedId = search.department ?? tree.data?.[0]?.id;
  const selected = flat.find((f) => f.node.id === selectedId)?.node;

  const select = (id: string) => {
    navigate({ to: '/org', search: { department: id }, replace: true });
    // На узком экране карточка отдела стоит под схемой — прокручиваем к ней
    if (window.matchMedia('(max-width: 1279px)').matches) {
      requestAnimationFrame(() =>
        document.getElementById('department-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Структура компании"
        description="Отделы и подчинённость. Руководитель отдела автоматически согласует заявки своих сотрудников и видит историю изменений по своей ветке."
      />
      {tree.isPending ? (
        <Spinner />
      ) : tree.error ? (
        <ErrorBox error={tree.error} />
      ) : (
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
          <section className="panel overflow-x-auto px-4 py-5 sm:px-6" aria-label="Схема отделов">
            <div className="org-tree sm:min-w-[480px]">
              {tree.data.map((root) => (
                <TreeNode key={root.id} node={root} selectedId={selectedId} onSelect={select} root />
              ))}
            </div>
          </section>
          {selected && (
            <DepartmentPanel key={selected.id} department={selected} all={flat.map((f) => f.node)} />
          )}
        </div>
      )}
    </div>
  );
}

function TreeNode({
  node,
  selectedId,
  onSelect,
  root,
}: {
  node: DepartmentNode;
  selectedId?: string;
  onSelect: (id: string) => void;
  root?: boolean;
}) {
  const active = node.id === selectedId;
  const box = (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      aria-current={active ? 'true' : undefined}
      className={clsx(
        'relative my-1 flex w-full max-w-[460px] items-center gap-3 rounded-md border bg-surface px-3 py-2 text-left transition-colors',
        active
          ? 'border-graphite shadow-[inset_3px_0_0_var(--color-signal)]'
          : 'border-line hover:border-line-strong',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className={clsx('block truncate', root ? 'text-[15px] font-semibold' : 'font-medium')}>
          {node.name}
        </span>
        <span className="block truncate text-[12.5px] text-steel">
          {node.head ? node.head.fullName : 'Руководитель не назначен'}
        </span>
      </span>
      {!node.head && !root && (
        <CircleAlert className="size-4 shrink-0 text-[#b48700]" aria-label="Нет руководителя" />
      )}
      <span
        className="shrink-0 text-[13px] tabular-nums text-steel"
        title="Сотрудников, включая вложенные отделы"
      >
        {node.employeeCount}
      </span>
    </button>
  );
  if (root) {
    return (
      <div>
        {box}
        {node.children.length > 0 && (
          <ul>
            {node.children.map((c) => (
              <li key={c.id}>
                <TreeNode node={c} selectedId={selectedId} onSelect={onSelect} />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  return (
    <>
      {box}
      {node.children.length > 0 && (
        <ul>
          {node.children.map((c) => (
            <li key={c.id}>
              <TreeNode node={c} selectedId={selectedId} onSelect={onSelect} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function DepartmentPanel({ department, all }: { department: DepartmentNode; all: DepartmentNode[] }) {
  const can = useCan();
  const canManage = can('org.department.manage', department.path);
  const [mode, setMode] = useState<'view' | 'edit' | 'child'>('view');
  const staff = useQuery(employeesQuery({ departmentId: department.id, includeSubdepartments: true }));
  const parent = all.find((d) => d.id === department.parentId);

  return (
    <aside
      id="department-panel"
      className="panel scroll-mt-20 xl:sticky xl:top-20"
      aria-label={`Отдел ${department.name}`}
    >
      <div className="border-b border-line px-5 py-4">
        {parent && (
          <p className="mb-1 flex items-center gap-1 text-[12.5px] text-steel">
            {parent.name} <ChevronRight className="size-3" aria-hidden />
          </p>
        )}
        <h2 className="text-[19px] font-semibold tracking-[-0.01em]">{department.name}</h2>
        <p className="mt-0.5 text-steel">
          {department.employeeCount}{' '}
          {plural(department.employeeCount, ['сотрудник', 'сотрудника', 'сотрудников'])}
          {department.children.length > 0 &&
            `, ${department.children.length} ${plural(department.children.length, ['вложенный отдел', 'вложенных отдела', 'вложенных отделов'])}`}
        </p>
      </div>

      {mode === 'edit' ? (
        <DepartmentForm department={department} all={all} onDone={() => setMode('view')} />
      ) : mode === 'child' ? (
        <DepartmentForm parentId={department.id} all={all} onDone={() => setMode('view')} />
      ) : (
        <>
          <div className="border-b border-line px-5 py-4">
            <p className="field-label">Руководитель</p>
            {department.head ? (
              <Link
                to="/employees/$id"
                params={{ id: department.head.id }}
                className="flex items-center gap-3 hover:text-link"
              >
                <Avatar name={department.head.fullName} />
                <span>
                  <span className="block font-medium">{department.head.fullName}</span>
                  <span className="block text-[12.5px] text-steel">
                    {department.head.positionName ?? 'Должность не указана'}
                  </span>
                </span>
              </Link>
            ) : (
              <p className="text-[#7a5b00]">Не назначен — заявки сотрудников уйдут выше по структуре.</p>
            )}
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2 border-b border-line px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setMode('edit')}>
                Изменить отдел
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setMode('child')}>
                <Plus className="size-4" aria-hidden />
                Вложенный отдел
              </Button>
            </div>
          )}
          <div className="px-5 py-4">
            <p className="field-label">Сотрудники</p>
            {staff.isPending ? (
              <Spinner />
            ) : staff.data?.items.length ? (
              <ul className="-mx-2 max-h-[380px] overflow-y-auto">
                {staff.data.items.map((e) => (
                  <li key={e.id}>
                    <Link
                      to="/employees/$id"
                      params={{ id: e.id }}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-sunken"
                    >
                      <Avatar name={e.fullName} employeeId={e.id} size={28} />
                      <span className="min-w-0">
                        <span className="block truncate">{e.fullName}</span>
                        <span className="block truncate text-[12.5px] text-steel">
                          {e.positionName ?? 'Должность не указана'}
                          {e.departmentId !== department.id && e.departmentName
                            ? `, ${e.departmentName}`
                            : ''}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-steel">В отделе пока никого нет.</p>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

function DepartmentForm({
  department,
  parentId,
  all,
  onDone,
}: {
  department?: DepartmentNode;
  parentId?: string;
  all: DepartmentNode[];
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const people = useQuery(allEmployeesQuery);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const isNew = !department;
  // Нельзя выбрать родителем сам отдел или что-то внутри него
  const parents = all.filter(
    (d) => !department || (d.path !== department.path && !d.path.startsWith(`${department.path}.`)),
  );

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      isNew
        ? api<{ id: string }>('/org/departments', { method: 'POST', body })
        : api<{ id: string }>(`/org/departments/${department.id}`, { method: 'PATCH', body }),
    onSuccess: async (row) => {
      await qc.invalidateQueries({ queryKey: ['org'] });
      if (isNew) await navigate({ to: '/org', search: { department: row.id }, replace: true });
      onDone();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? null : err.message);
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => api(`/org/departments/${department?.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['org'] });
      await navigate({
        to: '/org',
        search: { department: department?.parentId ?? undefined },
        replace: true,
      });
    },
    onError: (err) => setFormError(err.message),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {
      name: fd.get('name'),
      headEmployeeId: fd.get('headEmployeeId') || null,
    };
    if (isNew) body.parentId = parentId;
    else if (department.parentId) body.parentId = fd.get('parentId');
    save.mutate(body);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-4">
      <p className="font-medium">{isNew ? 'Новый вложенный отдел' : 'Изменение отдела'}</p>
      <Field label="Название" error={errors.name}>
        {(p) => <Input {...p} name="name" defaultValue={department?.name} autoFocus required />}
      </Field>
      {!isNew && department.parentId && (
        <Field label="Входит в" error={errors.parentId}>
          {(p) => (
            <Select {...p} name="parentId" defaultValue={department.parentId ?? ''}>
              {parents.map((d) => (
                <option key={d.id} value={d.id}>
                  {' '.repeat(d.path.split('.').length - 1)}
                  {d.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      <Field
        label="Руководитель"
        error={errors.headEmployeeId}
        hint="Получит права руководителя на этот отдел и все вложенные"
      >
        {(p) => (
          <Select {...p} name="headEmployeeId" defaultValue={department?.head?.id ?? ''}>
            <option value="">Не назначен</option>
            {people.data?.items.map((e) => (
              <option key={e.id} value={e.id}>
                {e.fullName}
                {e.departmentName ? ` (${e.departmentName})` : ''}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {formError && (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-[13.5px] text-danger">
          {formError}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" loading={save.isPending}>
          {isNew ? 'Создать отдел' : 'Сохранить'}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Отмена
        </Button>
        {!isNew && department.parentId && (
          <Button
            variant="danger"
            className="ml-auto"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Отключить отдел
          </Button>
        )}
      </div>
    </form>
  );
}
